import React, { PropsWithChildren, ReactElement } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { errorReporter } from '@/services/errorReporter';

type Props = PropsWithChildren<unknown> & { fallback?: ReactElement<{ error?: unknown }> } & WithTranslation;

class ErrorBoundary extends React.Component<Props> {
  override state = { hasError: false, error: null as unknown };

  static getDerivedStateFromError(error: unknown) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error };
  }

  override componentDidCatch(error: unknown, errorInfo: React.ErrorInfo) {
    // Capture the true root cause here. Without this, the only thing Sentry sees
    // is whatever the fallback UI happens to throw downstream, masking the real
    // error that tripped the boundary.
    errorReporter.report(error, { extra: { componentStack: errorInfo.componentStack ?? undefined } });
  }

  override render() {
    const { t } = this.props;
    if (this.state.hasError) {
      // Clone fallback element with error prop if it exists
      if (this.props.fallback) {
        return React.cloneElement(this.props.fallback, { error: this.state.error });
      }
      return <h1>{t('common.error')}</h1>;
    }

    return this.props.children;
  }
}

export default withTranslation('web')(ErrorBoundary);
